import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

/**
 * Admin read and trigger endpoints for the TopDecked analytics ingest job
 * (ticket 1218427695140081). The backend computes durationMs, resolves
 * previousSuccessfulRecordCount, normalizes status to the lowercase values below,
 * and caps runs at TD_INGEST_HISTORY_PAGE_SIZE. The frontend computes nothing except
 * the display truncation of the ETag, and never infers status from a null record count.
 */

/** `running` is a row whose finishedAt is still null (visible during the post-trigger poll). */
export type TdIngestRunStatus = 'success' | 'noop' | 'refused' | 'failed' | 'running';

export interface TdIngestRun {
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: TdIngestRunStatus;
  etag: string | null;
  recordCount: number | null;
  droppedJunkNameCount: number | null;
  enumWarningCount: number | null;
  errorMessage: string | null;
}

export interface TdIngestHistoryResponse {
  cardAnalyticsRowCount: number;
  /** From the last successful run; admin-only diagnostic, never user-facing. */
  sourceLastModified: string | null;
  /** A row with finishedAt = null exists. The only gate for the Run now control. */
  runInProgress: boolean;
  lastSuccessful: {
    startedAt: string;
    recordCount: number;
    droppedJunkNameCount: number;
    enumWarningCount: number;
  } | null;
  /** Most recent refused row in the window. */
  lastRefusal: {
    startedAt: string;
    recordCount: number;
    previousSuccessfulRecordCount: number;
    errorMessage: string;
  } | null;
  /** Newest first, capped at TD_INGEST_HISTORY_PAGE_SIZE. */
  runs: TdIngestRun[];
}

export interface TdIngestRunAck {
  startedAt: string;
}

export const adminTopDeckedApi = createApi({
  reducerPath: 'adminTopDeckedApi',
  baseQuery: baseQuery,
  tagTypes: ['TopDeckedIngest'],
  endpoints: (builder) => ({
    getIngestHistory: builder.query<TdIngestHistoryResponse, void>({
      query: () => ({
        url: '/admin/topdecked/ingest-history',
        method: 'GET',
        requiresAuth: true,
        headers: { 'Cache-Control': 'no-cache' },
      }),
      providesTags: ['TopDeckedIngest'],
    }),
    /** 202 { startedAt }; 409 { error: 'run_in_progress' }; 403 for a non-admin session. */
    runIngest: builder.mutation<TdIngestRunAck, void>({
      query: () => ({
        url: '/admin/topdecked/ingest-run',
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['TopDeckedIngest'],
    }),
  }),
});

export const { useGetIngestHistoryQuery, useRunIngestMutation } = adminTopDeckedApi;
